<template>
  <div v-if="!loading && contract_manager">
    <h1 class="main-header">
      <translate translate-context="contract_managers"
                 :translate-params="{id: contract_manager.id, name: contract_manager.name}">
        Contract Manager #%{id} - %{name}
      </translate>
    </h1>

    <CCard>
      <CCardBody>
        <div class="detail-container">
          <div class="info-section mb-4">
            <h3 v-translate translate-context="contract_manager">Contract Manager Details</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label" v-translate>Name:</span>
                <span class="info-value">{{ contract_manager.name }}</span>
              </div>
              <div class="info-item" v-if="contract_manager.region">
                <span class="info-label" v-translate>Region:</span>
                <span class="info-value">{{ contract_manager.region }}</span>
              </div>
              <div class="info-item" v-if="contract_manager.currency">
                <span class="info-label" v-translate>Currency:</span>
                <span class="info-value">{{ contract_manager.currency }}</span>
              </div>
            </div>
          </div>

          <div class="navigation-section">
            <h3 v-translate translate-context="contract_manager">Manage</h3>
            <div class="button-grid">
              <CButton 
                color="success" 
                size="lg"
                @click="navigateTo('contract_manager.contracts')"
                class="nav-button"
                v-if="hasPerm('contract_manager.contract.list')">
                <CIcon name="cipDoc" class="mr-2"/>
                <span v-translate>Contracts</span>
              </CButton>
              
              <CButton 
                color="success" 
                size="lg"
                @click="navigateTo('contract_manager.users')"
                class="nav-button"
                v-if="hasPerm('contract_manager.collaborator.list')">
                <CIcon name="cipUsers" class="mr-2"/>
                <span v-translate>Users</span>
              </CButton>
              
              <CButton 
                color="success" 
                size="lg"
                @click="navigateTo('contract_manager.roles')"
                class="nav-button"
                v-if="hasPerm('contract_manager.role.list')">
                <CIcon name="cipShieldCheck" class="mr-2"/>
                <span v-translate>Roles</span>
              </CButton>
              
              <CButton 
                color="info" 
                size="lg"
                @click="navigateTo('contract_manager.edit')"
                class="nav-button"
                v-if="hasPerm('contract_manager.update')">
                <CIcon name="cipPencil" class="mr-2"/>
                <span v-translate>Edit</span>
              </CButton>
              
              <CButton 
                color="primary" 
                size="lg"
                @click="navigateToPicaServices()"
                class="nav-button"
                v-if="hasPerm('events.fields.contract_manager')">
                <CIcon name="cip-calendar" class="mr-2"/>
                <span v-translate>PicaServices</span>
              </CButton>
            </div>
          </div>

          <div class="stats-section mt-4" v-if="contract_manager.contracts">
            <h3 v-translate translate-context="contract_manager">Statistics</h3>
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-value">{{ contract_manager.contracts ? contract_manager.contracts.length : 0 }}</div>
                <div class="stat-label" v-translate>Total Contracts</div>
              </div>
              <div class="stat-card" v-if="contract_manager.users">
                <div class="stat-value">{{ contract_manager.users ? contract_manager.users.length : 0 }}</div>
                <div class="stat-label" v-translate>Total Users</div>
              </div>
              <div class="stat-card" v-if="contract_manager.roles">
                <div class="stat-value">{{ contract_manager.roles ? contract_manager.roles.length : 0 }}</div>
                <div class="stat-label" v-translate>Total Roles</div>
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {RecordNotFound} from "@/domain/core/exception/exceptions";

export default {
  name: 'DetailContractManager',
  data() {
    return {
      loading: true,
    }
  },
  async mounted() {
    try {
      await Promise.all([
        this.fetchConfig({type: 'contract_manager', id: this.$route.params.id}),
        this.fetchContractManager(this.$route.params.id),
        this.fetchContracts()
      ])
    } catch (e) {
      if (e instanceof RecordNotFound) {
        await this.$router.push({name: 'contract_manager.list'})
      }
    }
    this.loading = false
  },
  computed: {
    ...mapGetters('contractManager', [
      'contract_manager',
    ]),
  },
  methods: {
    ...mapActions('contractManager', [
      'fetchContractManager',
      'fetchContracts',
    ]),
    ...mapActions('login', [
      'fetchConfig'
    ]),
    ...mapActions('events', [
      'updateColumnFilterValue'
    ]),
    navigateTo(routeName) {
      this.$router.push({
        name: routeName,
        params: { id: this.contract_manager.id }
      })
    },
    async navigateToPicaServices() {
      // Set the contract manager filter in the events store
      await this.updateColumnFilterValue({
        contract_manager: this.contract_manager.name
      })
      
      // Navigate to events page
      this.$router.push({
        name: 'eventList',
        params: { contractId: 'all' }
      })
    }
  }
}
</script>

<style scoped>
.detail-container {
  padding: 1rem;
}

.info-section {
  border-bottom: 1px solid #e3e4e6;
  padding-bottom: 1.5rem;
}

.info-section h3 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-label {
  font-weight: 600;
  color: #636f83;
}

.info-value {
  color: #2c3e50;
}

.navigation-section h3 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.button-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.nav-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  font-weight: 500;
  transition: all 0.2s;
}

.nav-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.stats-section {
  border-top: 1px solid #e3e4e6;
  padding-top: 1.5rem;
}

.stats-section h3 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.stat-card {
  background: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  border: 1px solid #e3e4e6;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #321fdb;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #636f83;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .button-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>